import React, { useContext, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../../../context/ContextIndex";
import {
  DataGridTable,
  GridAction,
  ConfirmationPopup,
  notifyToast,
  ToastType,
  FormPage,
} from "../../../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../../../apis/pricing-api";
import { MdDelete, MdCreate } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../../../store/store-selector";
import { AppUtility } from "../../../../utils/utility";
import { getReplacementProduct, saveConsumableProduct } from "../../api-handler/api-handler";
import { getReplacementProductDefaultValue } from "../../replacement-product/create/schema";

const ConsumableProductList = () => {
  const { t } = useTranslation();
  const [consumableProductRecordList, setConsumableProductRecordList] = useState([]);
  const [dataGridPageSize, setDataGridPageSize] = useState(10);
  const [totalConsumableProducts, setTotalConsumableProducts] = useState(0);
  const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);

  const navigate = useNavigate();
  const spinnerCtx = useContext(DisplaySpinnerContext);

  const settingsConfig = useSelector(selectSessionConfig);

  const handleCreateConsumableProductClick = async () => {
    navigate("/consumableProduct");
  };

  const handleEditConsumableProductClick = async (param) => {
    let _navigationData = param.recordResponse;
    delete _navigationData.CreatedDate;
    delete _navigationData.ModifiedDate;
    delete _navigationData.CountryCode;
    delete _navigationData.BusinessCode;

    navigate(
      {
        pathname: "/consumableProduct",
        search: createSearchParams({
          consumableProductid: param.recordResponse.ConsumableProductId,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const dataGridSchema = [
    {
      field: "shortName",
      headerName: t("consumableProductList.shortName"),
      flex: 2,
    },
    {
      field: "longName",
      headerName: t("consumableProductList.longName"),
      flex: 2,
    },
    {
      field: "measurementUnit",
      headerName: t("consumableProductList.measurementUnit"),
      flex: 1,
    },
    {
      field: "contractCost",
      headerName: t("consumableProductList.contractCost"),
      flex: 1,
    },
    {
      field: "icabsCode",
      headerName: t("consumableProductList.icabsCode"),
      flex: 1,
    },
    {
      field: "toxic",
      headerName: t("consumableProductList.toxic"),
      flex: 1,
    },
    {
      field: "applicationUnit",
      headerName: t("consumableProductList.applicationUnit"),
      flex: 1,
    },
    {
      field: "applicationUnitCost",
      headerName: t("consumableProductList.applicationUnitCost"),
      flex: 1,
    },
    {
      field: "grossMargin",
      headerName: t("consumableProductList.grossMargin"),
      flex: 1,
    },
    {
      field: "recordResponse",
      hide: true,
    },
    {
      field: "action",
      headerName: t,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <GridAction actionsConfig={actionConfigs} row={params.row} />
        );
      },
    },
  ];

  const handleConfirm = async () => {
    let _filterClause = `&consumableproductid=${selectedRow.id}`;
    await fetchAvailablePezodeProductMap(_filterClause, selectedRow);
    setPopupOpen(false);
    setSelectedRow(null);
  };

  const handleCancel = () => {
    setSelectedRow(null);
    setPopupOpen(false);
  };

  const handleDeleteConsumableProductClick = (event) => {
    try {
      setSelectedRow(event);
      setPopupOpen(true);
    } catch (err) {
      alert(err);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setDataGridPageSize(newPageSize);
  };

  //show review modal
  const handleShowSelectedConsumableChanges = (event) => {
    let _navigationData = { previousPath: "/consumableproductlist" };
    navigate(
      {
        pathname: "/reviewdetails",
        search: createSearchParams({
          reviewFilter: `&locationkey=${event.id}`,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  const setConsumableProductList = async (consumableProductList) => {
    try {
      consumableProductList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
      let _consumableProductList = consumableProductList.map((item) => {
        return {
          id: item.ConsumableProductId,
          shortName: item.ShortName,
          longName: item.LongName,
          measurementUnit: item.MeasurementUnit,
          contractCost: item.ContractCost,
          icabsCode: item.ICABSCode,
          toxic: item.Toxic,
          applicationUnit: item.ApplicationUnit,
          applicationUnitCost: item.ApplicationUnitCost,
          grossMargin: item.GMMaterial,
          recordResponse: item,
        };
      });

      setConsumableProductRecordList(_consumableProductList);
      setTotalConsumableProducts(_consumableProductList.length);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      spinnerCtx.setLoadingIndicator(false);
      alert(err);
    }
  };

  const FetchConsumableProductList = async () => {
    try {
      let _lists = await PricingPortalAPI.getList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getConsumableProductList",
        "consumableproduct"
      );
      await setConsumableProductList(_lists);
    } catch (err) {
      alert(err);
    }
  };
  useEffect(() => {

    spinnerCtx.setLoadingIndicator(true);
    FetchConsumableProductList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAvailablePezodeProductMap = async (filter, param) => {
    try {
      let _availablePezode = await PricingPortalAPI.getFilterList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getPezodeProductMap",
        "pezodeproductmap",
        filter
      );

      if (_availablePezode && _availablePezode.length > 0) {
        notifyToast("Pezode product mapping exists with this consumable product", ToastType.ERROR);
      } else {
        await deleteConsumableProduct(param);
      }
    } catch (e) {
      alert(e);
    }
  };
  const formPreviousDataForDelete = async (param, editConsumableProductId) => {
    try {
      let _previousState = {}
      _previousState.consumableProduct = param.recordResponse;
      _previousState.Deleted = true;
      delete _previousState.consumableProduct.CreatedDate;
      delete _previousState.consumableProduct.ModifiedDate;
      delete _previousState.consumableProduct.CountryCode;
      delete _previousState.consumableProduct.BusinessCode;
      delete _previousState.consumableProduct.Version;
      delete _previousState.consumableProduct.ConsumableProductId;
      delete _previousState.consumableProduct.Status;

      let response = await getReplacementProduct(editConsumableProductId, settingsConfig);
      _previousState.replacementProducts = response.map((item, index) => ({
        id: index,
        ...getReplacementProductDefaultValue(item)
      }));
      return _previousState ? _previousState : null;
    } catch (e) {
      alert(e);
    }
  };
  const deleteConsumableProduct = async (param) => {
    try {
      spinnerCtx.setLoadingIndicator(true);
      let editConsumableProductId = param.id;
      let data = await formPreviousDataForDelete(param, editConsumableProductId);
      const status = await saveConsumableProduct(data, {
        editConsumableProductId,
        settingsConfig,
        formdata: data
      });

      if (status?.responseStatus === 200) {
        notifyToast("Sent for Approval", ToastType.SUCCESS);
        await FetchConsumableProductList();
      } else {
        notifyToast(status.errorMessage ? status.errorMessage : "Consumable product deletion failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

  const buttonConfigs = [
    {
      buttonText: <AddIcon />,
      buttonType: "filled",
      onClick: handleCreateConsumableProductClick,
      buttonColor: "#A62B23",
      textColor: "#fff",
      action: "create"
    },
    {
      buttonText: <SystemUpdateAltIcon />,
      buttonType: "outlined",
      onClick: () => {
        AppUtility.handleDownload(consumableProductRecordList, "consumableproductlist.xlsx");
      },
      buttonColor: "#A62B23",
      textColor: "#A62B23",
      action: "delete"
    },
  ];
  //action configs for edit and delete record
  const actionConfigs = [
    {
      onClick: handleShowSelectedConsumableChanges,
      action: "review"
    },
    {
      icon: MdCreate,
      onClick: handleEditConsumableProductClick,
      buttonColor: "#EEEEE",
      action: "edit"
    },
    {
      icon: MdDelete,
      onClick: handleDeleteConsumableProductClick,
      buttonColor: "#A62B23",
      action: "delete"
    },
  ];
  return (
    <FormPage isList={true}>
      <DataGridTable
        rows={consumableProductRecordList}
        columns={dataGridSchema}
        pageSize={dataGridPageSize}
        pageSizeOptions={gridPageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        totalRowCount={totalConsumableProducts}
        toolbarHeader={t("consumableProductList.header")}
        toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
        buttonConfigs={buttonConfigs}
      />
      <ConfirmationPopup
        isOpen={popupOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        confirmationText={t("popup.deleteConsumableProductConfirmationMessage")}
        eventObj={selectedRow}
      />
    </FormPage>
  );
};

export default ConsumableProductList;
